import React from 'react';
import { Phone, Video, Voicemail, Users, BarChart, Headphones } from 'lucide-react';
import { motion } from 'framer-motion';

export const Telephonie = () => {
  const features = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "VoIP Professionnel",
      description: "Téléphonie IP de dernière génération avec qualité HD"
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Visioconférence",
      description: "Réunions virtuelles avec vidéo haute définition"
    },
    {
      icon: <Voicemail className="w-6 h-6" />,
      title: "Messagerie vocale",
      description: "Boîte vocale intelligente avec transcription"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Standard auto",
      description: "Routage d'appels automatisé et personnalisable"
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "Rapports détaillés",
      description: "Statistiques d'appels et analyses avancées"
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: "Centre d'appels",
      description: "Solution complète pour votre service client"
    }
  ];

  return (
    <section id="telephonie" className="py-24 relative overflow-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0 network-lines opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1720962158849-2c3b22499d2b" 
                alt="Système téléphonique"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
              
              {/* Overlay Badge */}
              <div className="absolute bottom-8 left-8 right-8 glass rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#FF6B35] rounded-full flex items-center justify-center">
                    <Phone className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-[#FF6B35]">99.9%</div>
                    <div className="text-sm text-gray-400">Disponibilité garantie</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
              Communication Moderne
            </span>
            <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
              Systèmes<span className="text-[#FF6B35]"> Téléphoniques</span>
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Transformez votre communication d'entreprise avec nos solutions VoIP avancées. Réduisez vos coûts tout en améliorant la qualité et les fonctionnalités.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-[#121212]/50 border border-white/5 rounded-lg hover:border-[#FF6B35]/30 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center text-[#FF6B35] flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-sm">{feature.title}</h4>
                    <p className="text-xs text-gray-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="space-y-3">
              {[
                "Économisez jusqu'à 60% sur vos coûts de téléphonie",
                "Installation rapide sans interruption de service",
                "Support technique dédié et formation incluse"
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>

            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-8 bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)]"
            >
              Demander une démo
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};